import React, { useState, useEffect } from 'react';
import IconLink from '@/assets/icons/icon-link.svg?react';
import IconLinked from '@/assets/icons/icon-linked.svg?react';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import clsx from 'clsx';

interface PaddingMarginValues {
  all: number;
  top: number;
  right: number;
  bottom: number;
  left: number;
}

interface PaddingMarginFieldsProps {
  className?: string;
  value: PaddingMarginValues;
  onChange: (values: PaddingMarginValues) => void;
  onChangeComplete?: (values: PaddingMarginValues, initialValues: PaddingMarginValues) => void;
  variant?: 'locked' | 'unlocked';
  link?: 'default' | 'empty';
  label?: string;
  setVariant?: (variant: 'locked' | 'unlocked') => void;
  onFocus?: () => void;
  onBlur?: () => void;
  min?: number;
  max?: number;
}

export const PaddingMarginFields: React.FC<PaddingMarginFieldsProps> = ({
  value,
  className = '',
  onChange = () => {},
  onChangeComplete,
  onFocus = () => {},
  onBlur = () => {},
  link = 'default',
  variant = 'locked',
  label = 'Padding',
  min = 0,
  max = 100,
}) => {
  const [isLocked, setIsLocked] = useState(variant === 'locked');
  const [initialValues, setInitialValues] = useState<PaddingMarginValues>({
    ...value,
  });
  // Store locked value (all) independently
  const [lockedValue, setLockedValue] = useState<number>(value.all);
  // Store unlocked values independently
  const [unlockedValues, setUnlockedValues] = useState<Omit<PaddingMarginValues, 'all'>>({
    top: value.top,
    right: value.right,
    bottom: value.bottom,
    left: value.left,
  });

  // Sync incoming values but preserve independent state
  useEffect(() => {
    if (value) {
      if (isLocked) {
        setLockedValue(value.all);
      } else {
        setUnlockedValues({
          top: value.top,
          right: value.right,
          bottom: value.bottom,
          left: value.left,
        });
      }
    }
  }, [value, isLocked]);

  useEffect(() => {
    setIsLocked(variant === 'locked');
  }, [variant]);

  const setNewValues = (key: keyof PaddingMarginValues, val: number) => {
    if (val > max) val = max;
    if (val < min) val = min;

    let newValues: PaddingMarginValues;

    if (isLocked && key === 'all') {
      // Update locked value and sync unlocked values
      setLockedValue(val);
      setUnlockedValues({ top: val, right: val, bottom: val, left: val });
      newValues = { all: val, top: val, right: val, bottom: val, left: val };
    } else if (!isLocked && key !== 'all') {
      // Update unlocked values
      const updatedUnlocked = { ...unlockedValues, [key]: val };
      setUnlockedValues(updatedUnlocked);
      newValues = { all: lockedValue, ...updatedUnlocked };
    } else {
      // Fallback
      newValues = { all: lockedValue, ...unlockedValues };
    }

    return newValues;
  };

  const handleOnChangeInput = (
    key: keyof PaddingMarginValues,
    val: number,
    e: { key?: string },
  ) => {
    const newValues = setNewValues(key, val);
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      onChange(newValues);
    }
  };

  const handleOnChangeSlider = (key: keyof PaddingMarginValues, val: number) => {
    const newValues = setNewValues(key, val);
    onChange(newValues);
  };
  const handleToggleLock = () => {
    if (isLocked) {
      // Switch to unlocked: send current unlocked values to craft.js
      const newValues = { all: lockedValue, ...unlockedValues };
      setIsLocked(false);
      onChangeComplete?.(newValues, initialValues);
      setInitialValues(newValues);
    } else {
      // Switch to locked: send current locked value to craft.js
      const newValues = {
        all: lockedValue,
        top: lockedValue,
        right: lockedValue,
        bottom: lockedValue,
        left: lockedValue,
      };
      setIsLocked(true);
      onChangeComplete?.(newValues, initialValues);
      setInitialValues(newValues);
    }
  };

  const handleChangeComplete = () => {
    const currentValues = isLocked
      ? {
          all: lockedValue,
          top: lockedValue,
          right: lockedValue,
          bottom: lockedValue,
          left: lockedValue,
        }
      : { all: lockedValue, ...unlockedValues };
    onChangeComplete?.(currentValues, initialValues);
    setInitialValues(currentValues);
  };

  return (
    <div
      className={clsx('flex w-full items-center justify-between gap-3', {
        [className]: className,
      })}
    >
      <p className="text-button-sm min-w-12 font-medium text-neutral-500">{label}</p>

      <div className="flex w-full max-w-[155px] justify-end">
        {isLocked ? (
          <div className="flex w-full items-center justify-end gap-3">
            {link === 'default' && (
              <IconLinked
                className="h-4.5 min-h-4.5 w-4.5 min-w-4.5 cursor-pointer text-indigo-500"
                onClick={handleToggleLock}
              />
            )}
            <Input
              pattern="\d{1,5}"
              maxLength={5}
              className="h-8 min-h-8 w-10 min-w-10 text-center"
              value={String(lockedValue)}
              onChange={(e: React.ChangeEvent<HTMLInputElement> & { key?: string }) => {
                const newValue = e.currentTarget.value;
                if (newValue === '') {
                  setLockedValue(0);
                  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                    onChange({ all: 0, top: 0, right: 0, bottom: 0, left: 0 });
                  }
                  return;
                }
                const numeric = parseInt(newValue, 10);
                if (!isNaN(numeric)) {
                  handleOnChangeInput('all', numeric, e);
                }
              }}
              onFocus={onFocus}
              onBlur={onBlur}
              onChangeComplete={handleChangeComplete}
            />
            <Slider
              className={clsx('w-full max-w-[76px]')}
              value={[lockedValue]}
              max={max}
              min={min}
              step={1}
              onValueChange={([val]) => handleOnChangeSlider('all', val)}
              onFocus={onFocus}
              onBlur={onBlur}
              onChangeComplete={handleChangeComplete}
            />
          </div>
        ) : (
          <div className="flex w-full items-center gap-3">
            {link === 'default' && (
              <IconLink
                className="h-4.5 min-h-4.5 w-4.5 min-w-4.5 cursor-pointer text-indigo-500"
                onClick={handleToggleLock}
              />
            )}
            <div className="flex items-center">
              {(['top', 'right', 'bottom', 'left'] as const).map((side, index) => (
                <Input
                  pattern="\d{1,5}"
                  max={max}
                  min={min}
                  maxLength={5}
                  key={side}
                  className={`h-8 w-8 p-1 text-center ${
                    index === 0
                      ? 'rounded-r-none border-r border-neutral-100'
                      : index === 3
                        ? 'rounded-l-none'
                        : 'rounded-none border-r border-neutral-100'
                  }`}
                  value={String(unlockedValues[side])}
                  onChange={(e: React.ChangeEvent<HTMLInputElement> & { key?: string }) => {
                    const newValue = e.currentTarget.value;
                    if (newValue === '') {
                      setUnlockedValues((prev) => ({ ...prev, [side]: 0 }));
                      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                        const newValues = { all: lockedValue, ...unlockedValues, [side]: 0 };
                        onChange(newValues);
                      }
                      return;
                    }
                    const numeric = parseInt(newValue, 10);
                    if (!isNaN(numeric)) {
                      handleOnChangeInput(side, numeric, e);
                    }
                  }}
                  onChangeComplete={() => {
                    handleChangeComplete();
                  }}
                  onFocus={onFocus}
                  onBlur={onBlur}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
